import { Box, Select, Text } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import useSwr from "swr";
import { branchApi } from "../../apis";
import { IAuth } from "../../interfaces/IAuth";
import { useBusiness } from "../../pages/sales-invoice/new";
import { dateTimeFormat } from "../../utils/date";
import { renderBusinessName } from "../../utils/render-business-name";
import { renderName } from "../../utils/render-name";
import fieldClasses from "./invoice-form-fields.module.css";

type Props = { form: UseFormReturnType<any> };

function Kv({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <Box className={fieldClasses.kv}>
      <Text className={fieldClasses.label}>{label}</Text>
      {typeof children === "string" || typeof children === "number" ? (
        <Text className={mono ? fieldClasses.mono : fieldClasses.value}>{children}</Text>
      ) : (
        children
      )}
    </Box>
  );
}

export function InvoiceSenderForm({ form }: Props) {
  const { t } = useTranslation("invoice");
  const now = new Date();
  const params = useParams();
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);
  const { bankAccounts = [] } = useSelector((state: any) => state.general);
  const { business } = useBusiness();

  useEffect(() => {
    if (business?.inAccount?.id) {
      form.setFieldValue("senderAccId", business.inAccount.id);
    } else {
      form.setFieldValue("senderAccId", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business]);

  const [query, setQuery] = useState<string>("");

  const { data: businessList } = useSwr(
    user?.currentBusiness.id ? ["branch-select", user.currentBusiness.id, query] : null,
    user?.currentBusiness.id
      ? async () => await branchApi.select({ businessId: user.currentBusiness.id, query: query })
      : null,
    {
      fallback: [],
    },
  );

  useEffect(() => {
    if (businessList?.length) {
      const headOfficeBranch = businessList.find(
        (item: { branch: { type: string } }) => item.branch.type === "HEAD_OFFICE",
      );
      if (headOfficeBranch) {
        form.setFieldValue("senderBranchId", headOfficeBranch.branch.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessList]);

  const onSearchBranch = async (query: string) => {
    let timeout: any;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(async () => {
      setQuery(query);
    }, 1000);
  };

  const branchOptions =
    businessList?.map((item: any) => ({
      value: item?.branch?.id,
      label: `${item?.branch?.name ?? "-"}`,
      branchAddress: `${item?.branch?.branchAddress ?? ""}`,
    })) ?? [];

  const bankAccountOptions = (bankAccounts ?? []).map((item: any) => ({
    value: item.id,
    label: `${item.name} ${item.number}`,
  }));

  const senderBusiness = params.id === "new" ? user?.currentBusiness : form.values?.senderBusiness;
  const senderPartner = params.id === "new" ? user?.partner : form.values?.senderBusiness?.partner;
  const senderName = renderBusinessName(senderBusiness);
  const senderReg =
    params.id === "new"
      ? user?.partner?.regNumber ?? user?.currentBusiness?.regNumber
      : form.values?.senderBusiness?.regNumber ?? senderPartner?.regNumber;
  const senderCode =
    params.id === "new" ? user?.currentBusiness?.refCode : form.values?.senderBusiness?.refCode;

  return (
    <Box>
      {senderBusiness ? (
        <Text ta="right" mb="md">
          <Text span fw={500} fz="sm">
            {senderName}{" "}
          </Text>
          {senderReg ? (
            <Text span className={fieldClasses.mono} c="dimmed">
              {senderReg}
            </Text>
          ) : null}
          {senderReg && senderCode ? (
            <Text span c="dimmed" fz="sm">
              {" · "}
            </Text>
          ) : null}
          {senderCode ? (
            <Text span className={fieldClasses.mono} c="dimmed">
              {senderCode}
            </Text>
          ) : null}
          {!senderReg && !senderCode ? (
            <Text span className={fieldClasses.mono} c="dimmed" fz="sm">
              -
            </Text>
          ) : null}
        </Text>
      ) : null}

      <Box className={fieldClasses.fields3}>
        <Kv label={t("form.invoiceDate")}>
          <Text className={fieldClasses.date}>
            {dateTimeFormat(params.id === "new" ? now : form.values.createdAt)}
          </Text>
        </Kv>

        <Select
          searchable
          clearable
          label={t("form.senderBranchLabel")}
          placeholder={t("form.senderBranchLabel")}
          onSearchChange={(e) => {
            onSearchBranch(e);
          }}
          data={branchOptions}
          renderOption={({ option }) => {
            const opt = branchOptions.find((o: any) => o.value === option.value);
            return (
              <Box>
                <Text size="sm">{option.label}</Text>
                <Text size="xs" c="dimmed">
                  {(opt as any)?.branchAddress}
                </Text>
              </Box>
            );
          }}
          required
          {...form.getInputProps("senderBranchId")}
        />

        <Select
          searchable
          clearable
          label={t("form.receiverAccLabel")}
          placeholder={t("form.receiverAccLabel")}
          data={bankAccountOptions}
          renderOption={({ option }) => {
            const acc = (bankAccounts ?? []).find((item: any) => item.id === option.value);
            return (
              <Box>
                <Text size="sm">{option.label}</Text>
                <Text size="xs" c="dimmed">
                  {t("common:accountNumberShort")}: {acc?.number} / {acc?.shortName} {acc?.bankName}
                </Text>
              </Box>
            );
          }}
          required
          disabled={business?.inAccount?.id}
          {...form.getInputProps("senderAccId")}
        />

        <Kv label={t("form.salesOrder")}>{form.values?.salesOrderCode ?? "-"}</Kv>
        <Kv label={t("form.purchaseOrder")}>{form.values?.purchaseOrderCode ?? "-"}</Kv>
        <Kv label={t("form.financeStaff")}>{renderName(user) || "-"}</Kv>
      </Box>
    </Box>
  );
}
